import { useEffect, useState } from "react";
import styled from "styled-components";
import BarChartItem from "components/BarChartItem";
import CustomSelect from "components/CustomSelect";
import { SingleValue } from "react-select";
import { getMaxCompetitorPrice } from "utils/getMaxCompetitorPrice";
import { useMemo } from "react";
import MathModal from "components/MathModal";

export interface ValueType {
  _id: string;
  label: string;
  key: string;
  value: string;
  description: string;
  category: string;
  array: boolean;
}

export interface ProviderType extends Omit<ValueType, "value"> {
  value: number[];
}

export interface HouseholdType {
  label: string;
  value: number;
  id: number;
}

const HOUSEHOLDS = [
  {
    id: 0,
    value: 0,
    label: "Leilighet (10.899 kWh i året)",
  },
  {
    id: 1,
    value: 1,
    label: "Rekkehus (17.090 kWh i året)",
  },
  {
    id: 2,
    value: 2,
    label: "Enebolig (25.776 kWh i året)",
  },
];

const App = () => {
  const [providers, setProviders] = useState<ProviderType[]>([]);

  const [selectedProvider, setSelectedProvider] =
    useState<SingleValue<ProviderType>>(null);

  const [selectedHouseholdType, setSelectedHouseholdType] = useState<
    SingleValue<HouseholdType>
  >(HOUSEHOLDS[2]);

  const [isModalOpen, setModalOpen] = useState(false);

  const getValues = async () => {
      const data = await fetch(`${new URL(window.location.href).origin}/values/priceBenchmark`);
    const values: ValueType[] = await data.json();
    const providers = values.map((provider) => ({
      ...provider,
      label: provider.key,
      value: provider.value
        .split(",")
        .map((value) => parseInt(value.trim().replace(/\s/g, ""))),
    }));

    setProviders(providers);
  };

  useEffect(() => {
    getValues();
  }, []);

  const handleSelectCompany = (newValue: SingleValue<ProviderType>) => {
    setSelectedProvider(newValue);
  };

  const handleSelectHouseholdType = (newValue: SingleValue<HouseholdType>) => {
    setSelectedHouseholdType(newValue);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const getBarFillPercentage = (value: number, maxPrice: number) => {
    return ((value - (maxPrice - value) / 6) / maxPrice!) * 100;
  };

  const defaultCompetitors = useMemo(() => {
    const competitors = [] as ProviderType[];

    if (window.screen.width >= 768) {
      providers.forEach((provider) => {
        if (
          provider.category === "priceBenchmark" &&
          (provider.key === "Fortum" ||
            provider.key === "Hafslund" ||
            provider.key === "Tibber" ||
            provider.key === "Agva")
        ) {
          return competitors.push(provider);
        }
      });
    }

    if (window.screen.width < 768) {
      providers.forEach((provider) => {
        if (
          provider.category === "priceBenchmark" &&
          (provider.key === "Fortum" ||
            provider.key === "Hafslund" ||
            provider.key === "Tibber")
        ) {
          return competitors.push(provider);
        }
      });
    }

    return [
      ...competitors
        .sort((a: any, b: any) => {
          return (
            a.value[selectedHouseholdType!.value] -
            b.value[selectedHouseholdType!.value]
          );
        })
        .reverse(),
    ];
  }, [selectedHouseholdType?.value, providers.length]);

  const vibb = providers.find((provider) => {
    if (provider.category === "priceBenchmark" && provider.key === "Vibb") {
      return { ...provider, label: provider.key };

    }
  });

  const maxPrice = useMemo(() => {
    if (!vibb || !defaultCompetitors.length) return 0;

    if (!selectedProvider) {
      return getMaxCompetitorPrice(
        [...defaultCompetitors, vibb],
        selectedHouseholdType!.value
      );
    }

    if (selectedProvider) {
      return getMaxCompetitorPrice(
        [selectedProvider, vibb],
        selectedHouseholdType!.value
      );
    }
  }, [selectedProvider?._id, providers.length, selectedHouseholdType?.id]);

  if (!defaultCompetitors || !defaultCompetitors.length || !vibb)
    return <div />;

  const priceDiff = !!selectedProvider
    ? selectedProvider.value[selectedHouseholdType!.value] -
      vibb.value[selectedHouseholdType!.value]
    : defaultCompetitors.reduce(function (avg, competitor, _, { length }) {
        return (
          avg +
          Math.round(competitor.value[selectedHouseholdType!.value] / length)
        );
      }, 0) - vibb.value[selectedHouseholdType!.value];

  return (
    <>
      <MathModal isOpen={isModalOpen} onClose={handleModalClose} />
      <ScreenWrap>
        <ContentLeft>
          <BarsContainer isProviderSelected={!!selectedProvider}>
            {!selectedProvider
              ? [...defaultCompetitors, vibb].map(({ key, value, _id }) => {
                  return (
                    <BarChartItem
                      key={_id}
                      name={key!}
                      price={value[selectedHouseholdType!.value]}
                      barFillerPercentage={getBarFillPercentage(
                        value[selectedHouseholdType!.value],
                        maxPrice!
                      )}
                      isVibb={key === "Vibb"}
                    />
                  );
                })
              : [selectedProvider, vibb].map(({ key, value, _id }) => {
                  return (
                    <BarChartItem
                      key={_id}
                      name={key!}
                      price={value[selectedHouseholdType!.value]}
                      barFillerPercentage={getBarFillPercentage(
                        value[selectedHouseholdType!.value],
                        maxPrice!
                      )}
                      isProviderSelected
                      isVibb={key === "Vibb"}
                    />
                  );
                })}
          </BarsContainer>
          <DesktopHidden>
            <HowMuch>
              I snitt kan du spare{" "}
              <span>
                {priceDiff}
                ,-
              </span>{" "}
              i året som Vibb-kunde
            </HowMuch>
            <MathButton onClick={handleModalOpen}>Regnestykket</MathButton>
          </DesktopHidden>
        </ContentLeft>

        <ContentRight>
          <Heading>
            Hvor mye kan du <span>spare i året</span> ved å bli kunde hos Vibb?
          </Heading>

          <SelectContainer>
            <CustomSelect
              options={providers.filter((provider) => provider.key !== "Vibb")}
              value={selectedProvider}
              onChange={handleSelectCompany}
              id="providers-select"
              instanceId="providers-select"
              inputId="providers-select"
              placeholder="Velg ditt strømselskap"
              isSearchable
              isClearable
            />
            <CustomSelect
              options={HOUSEHOLDS}
              value={selectedHouseholdType}
              onChange={handleSelectHouseholdType}
              id="household-type-select"
              instanceId="household-type-select"
              inputId="household-type-select"
              placeholder="Velg din boligtype"
              isSearchable={false}
            />
          </SelectContainer>
          <MobileHidden>
            <HowMuch>
              {priceDiff > 0 ? (
                <>
                  I snitt kan du spare <span>{priceDiff},-</span> i året som
                  Vibb-kunde
                </>
              ) : (
                <>
                  I snitt koster det bare <span>{-priceDiff},-</span> mer i året
                  som Vibb-kunde.
                </>
              )}
            </HowMuch>
            <MathButton onClick={handleModalOpen}>Regnestykket</MathButton>
          </MobileHidden>
        </ContentRight>
      </ScreenWrap>
    </>
  );
};

export default App;

const ScreenWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1300px;
  padding: 150px 0;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 38px 24px;
  }
`;

const Heading = styled.h1`
  font-size: 43px;
  line-height: 55px;
  font-family: "RiposteBold";
  margin-bottom: 20px;
  margin-top: 0;
  color: #43e695;
  & span {
    color: #000;
  }

  @media (max-width: 768px) {
    font-size: 34px;
  }
`;

const HowMuch = styled.div`
  font-size: 25px;
  line-height: 44px;
  font-family: "RiposteRegular";
  margin-top: 15px;
  margin-bottom: 25px;

  & span {
    color: #43e695;
    text-decoration: underline;
    font-family: "RiposteBold";
  }

  @media (max-width: 768px) {
    line-height: 33px;
  }
`;

const SelectContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: 0px;
    grid-row-gap: 15px;
    margin-bottom: 35px;
  }
`;

const ContentLeft = styled.div`
  flex: 1;
  padding: 0 36px;

  @media (max-width: 768px) {
    order: 2;
    padding: 0;
  }
`;

const BarsContainer = styled.div<{ isProviderSelected: boolean }>`
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: 26px;
    justify-content: ${({ isProviderSelected }) =>
      isProviderSelected ? "center" : "space-between"};
  }
`;

const ContentRight = styled.div`
  flex: 1.5;
  padding: 0 36px;

  @media (max-width: 768px) {
    order: 1;
    padding: 0;
  }
`;

const MobileHidden = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;
const DesktopHidden = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`;

const MathButton = styled.div`
  width: 160px;
  height: 40px;
  border-radius: 50px;
  outline: 0;
  background: #fff;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #fbfbfb;
  }
`;
